/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant, LocalDateTime } from 'js-joda';
import { core , safeToNumber } from '@tock/coretypes';
import _ from 'lodash';

/* The PastPurchase type is excessively large for the purposes of
 * displaying transaction history. Convert to this type to only pass
 * relevant data to the component.
 */
interface CombinedPurchaseHistory {
  id: number;
  purchaseTimestamp: Long;
  transferred: boolean | null;
  firstTransferredTo: core.PatronPublic | null;
  isTransferredFrom: boolean | null;
  transferredFromEmail: string | null;
  cancelledOrRefunded: boolean | null;
  isGiftCardPurchase: boolean;
  latestPurchase: boolean | null;
  chaseUltimateRewardsPoints: core.ChaseUltimateRewardsPoints | null;
  businessName: string;
  ticketTypeName: string;
  ticketDateTime: string;
  currencyCode: string;
  locale: string;
  amountPaidCents: number;
}

const mapPurchasePropsToCombinedHistory = (history: any): CombinedPurchaseHistory[] =>
  history
    ? _.map(history, (item) => ({
        id: item.id,
        purchaseTimestamp: item.purchaseTimestamp,
        transferred: item.transferred,
        firstTransferredTo: item.firstTransferredTo,
        isTransferredFrom: item.isTransferredFrom,
        transferredFromEmail: item.transferredFromEmail,
        cancelledOrRefunded: item.cancelledOrRefunded,
        isGiftCardPurchase: false,
        latestPurchase: item.latestPurchase,
        chaseUltimateRewardsPoints: item.chaseUltimateRewardsPoints,
        businessName: item.business ? item.business.name : '',
        ticketTypeName: item.ticketTypeName,
        ticketDateTime: item.ticketDateTime,
        currencyCode: item.business ? item.business.currencyCode : '',
        locale: item.business ? item.business.locale : '',
        amountPaidCents: item.amountPaidCents,
      }))
    : [];

// ticketDateTime needs to be string for giftcard
const mapGiftCardPropsToCombinedHistory = (history: any): CombinedPurchaseHistory[] =>
  history
    ? _.map(history, (item) => ({
        id: item.id,
        purchaseTimestamp: item.createdAtSecs,
        transferred: null,
        firstTransferredTo: null,
        isTransferredFrom: null,
        transferredFromEmail: null,
        cancelledOrRefunded: item.transactionType === 'VOID_LOAD' ? true : false,
        isGiftCardPurchase: true,
        latestPurchase: true,
        chaseUltimateRewardsPoints: null,
        businessName: 'Tock',
        ticketTypeName: 'Gift card purchase',
        ticketDateTime: LocalDateTime.ofInstant(
          Instant.ofEpochMilli(item.createdAtSecs)
        ).toString(),
        currencyCode: 'USD',
        locale: 'en-us',
        amountPaidCents: item.amountCents,
      }))
    : [];

const combineTransactionTypes = (
  purchaseHistories: core.PastPurchase[] | null,
  legacyTransfers: core.PastPurchase[] | null,
  giftCardHistory: core.TockGiftCardPurchaseHistory[] | undefined
): CombinedPurchaseHistory[] => {
  let initializedHistories = mapPurchasePropsToCombinedHistory(purchaseHistories);

  initializedHistories = _.uniqBy(
    _.concat(initializedHistories, mapPurchasePropsToCombinedHistory(legacyTransfers)),
    (element) => safeToNumber(element.id)
  );

  initializedHistories = _.concat(
    initializedHistories,
    mapGiftCardPropsToCombinedHistory(giftCardHistory)
  );
  initializedHistories = sortHistory(initializedHistories);
  return initializedHistories;
};

const combineAndSortPastPurchases = (
  purchaseHistories: core.PastPurchase[] | null,
  legacyTransfers: core.PastPurchase[] | null
): core.PastPurchase[] => {
  let initializedHistories = purchaseHistories || [];
  const initializedLegacyTransfers = legacyTransfers || [];

  initializedHistories = _.uniqBy(
    _.concat(initializedHistories, initializedLegacyTransfers),
    (element) => safeToNumber(element.id)
  );

  initializedHistories = sortHistory(initializedHistories);
  return initializedHistories;
};

const sortHistory = (purchaseHistory: any[]) =>
  _.reverse(
    _.sortBy(purchaseHistory, (history) => {
      return history.ticketDateTime;
    })
  );

const chaseURPurchase = (inChaseUR: boolean) => (purchase: any) =>
  purchase.latestPurchase && inChaseUR
    ? !!purchase.chaseUltimateRewardsPoints
    : !purchase.chaseUltimateRewardsPoints;

export {
  combineTransactionTypes,
  combineAndSortPastPurchases,
  CombinedPurchaseHistory,
  chaseURPurchase,
};
